import { useState } from "react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { SrIcon } from "@/components/ui/SrIcon";
import { formatNOK } from "@/lib/format";
import { PHASE_LABELS, type ProjectPhase } from "@/lib/offer-types";
import type { BudgetLineRow } from "@/hooks/use-project-detail";
import { useOfferLineTemplates } from "@/hooks/use-offers";
import {
  useUpdateBudgetLine,
  useDeleteBudgetLine,
  type BudgetLineUpdate,
} from "@/hooks/use-budget-lines";
import { TemplatePicker } from "./TemplatePicker";
import { deriveHours } from "@/lib/budget-hours";
import { calcActualCostVariancePct } from "@/lib/budget-line-actual-cost";
import { VarianceBadge } from "@/components/projects/VarianceBadge";

const UNITS = [
  { value: "t", label: "t" },
  { value: "d", label: "d" },
  { value: "stk", label: "stk" },
  { value: "fast", label: "fast" },
];

interface Props {
  bl: BudgetLineRow;
  projectId: string;
  editing: boolean;
  /** VIB-1295: påløpt kostnad (Σ approved bilag-allokeringer) for denne linjen. */
  actualCost?: number;
}

/**
 * VIB-586: Linje i Tilbud-fanen. Read-only for vanlige brukere; superadmin
 * får inline-redigering når `editing` er aktivert. Endringer skrives
 * direkte til budget_lines via mutations på blur/select-change og deler
 * cache med Timetildeling-fanen.
 */
export function OfferLineEditRow({ bl, projectId, editing, actualCost }: Props) {
  const updateMut = useUpdateBudgetLine();
  const deleteMut = useDeleteBudgetLine();
  const { data: templates = [] } = useOfferLineTemplates();

  // Lokal state for redigerings-input — committes ved blur/change.
  // Vis tomt felt for 0/manglende verdi så en stående «0» ikke blir liggende
  // foran innskrevne sifre (feedback 23.06: "taster man 2000 blir det 0200").
  const numInput = (n: number | null | undefined) => (n ? String(n) : "");
  const [description, setDescription] = useState(bl.description ?? "");
  const [quantity, setQuantity] = useState<string>(numInput(bl.quantity));
  const [amount, setAmount] = useState<string>(numInput(bl.amount));
  const [estCost, setEstCost] = useState<string>(numInput(bl.estimated_cost));

  const apply = (patch: BudgetLineUpdate) => {
    updateMut.mutate({ id: bl.id, project_id: projectId, patch });
  };

  if (!editing) {
    return (
      <tr className="border-b border-border/40">
        <td className="px-4 py-2 pl-8">
          <div>{bl.description}</div>
          {bl.template_name && (
            <div className="text-[10px] text-muted-foreground">
              ↳ {bl.template_name}
            </div>
          )}
        </td>
        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
          {bl.cost_type === "internal" && (bl.quantity_hours ?? 0) > 0
            ? `${bl.quantity_hours}t`
            : "—"}
        </td>
        <td className="px-3 py-2 text-right tabular-nums">
          {formatNOK(bl.amount ?? 0)}
        </td>
        <td className="px-4 py-2 text-right tabular-nums">
          {bl.cost_type === "external" ? (
            formatNOK(bl.estimated_cost ?? 0)
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </td>
        <td className="px-4 py-2 text-right tabular-nums">
          {actualCost == null ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            <div className="flex items-center justify-end gap-1.5">
              <span
                className={
                  actualCost > (bl.estimated_cost ?? 0) ? "text-destructive font-medium" : undefined
                }
              >
                {formatNOK(actualCost)}
              </span>
              <VarianceBadge
                diffPct={calcActualCostVariancePct(actualCost, bl.estimated_cost)}
              />
            </div>
          )}
        </td>
      </tr>
    );
  }

  const isExternal = bl.cost_type === "external";

  return (
    <tr className="border-b border-border/40 align-top">
      <td className="px-4 py-1.5 pl-8">
        <div className="flex items-center gap-2">
          <Input
            value={description}
            className="h-8"
            onChange={(e) => setDescription(e.target.value)}
            onBlur={() => {
              if (description !== bl.description) apply({ description });
            }}
          />
          <Select
            value={bl.phase}
            onValueChange={(v) => apply({ phase: v as ProjectPhase })}
          >
            <SelectTrigger className="h-8 w-32 px-2 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(PHASE_LABELS) as ProjectPhase[]).map((p) => (
                <SelectItem key={p} value={p}>
                  {PHASE_LABELS[p]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={bl.cost_type ?? "internal"}
            onValueChange={(v) => {
              const next = v as "internal" | "external";
              apply({
                cost_type: next,
                quantity_hours: deriveHours(next, bl.unit, bl.quantity),
              });
            }}
          >
            <SelectTrigger className="h-8 w-24 px-2 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="internal">Intern</SelectItem>
              <SelectItem value="external">Kostnad</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={bl.unit ?? "t"}
            onValueChange={(v) => {
              apply({
                unit: v,
                quantity_hours: deriveHours(bl.cost_type ?? "internal", v, bl.quantity),
              });
            }}
          >
            <SelectTrigger className="h-8 w-16 px-2 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {UNITS.map((u) => (
                <SelectItem key={u.value} value={u.value}>
                  {u.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="mt-1 ml-1">
          <TemplatePicker
            templates={templates}
            currentId={bl.template_id ?? null}
            currentName={bl.template_name ?? null}
            variant="compact"
            onSelect={(t) => {
              if (t) {
                // Mal-valg overstyrer fase, cost_type og enhet — samme regel
                // som ImportBudgetDialog. quantity_hours re-deriveres.
                const nextCostType: "internal" | "external" =
                  t.cost_type === "external" ? "external" : "internal";
                const nextUnit = t.default_unit ?? bl.unit;
                apply({
                  template_id: t.id,
                  phase: t.phase as ProjectPhase,
                  cost_type: nextCostType,
                  unit: nextUnit,
                  quantity_hours: deriveHours(nextCostType, nextUnit, bl.quantity),
                });
              } else {
                apply({ template_id: null });
              }
            }}
          />
        </div>
      </td>
      <td className="px-3 py-1.5 text-right">
        <Input
          type="number"
          step="0.25"
          className="h-8 text-right tabular-nums w-16"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          onBlur={() => {
            const next = quantity === "" ? null : Number(quantity);
            if (next !== bl.quantity) {
              apply({
                quantity: next,
                quantity_hours: deriveHours(
                  bl.cost_type ?? "internal",
                  bl.unit,
                  next,
                ),
              });
            }
          }}
        />
      </td>
      <td className="px-3 py-1.5 text-right">
        <Input
          type="number"
          className="h-8 text-right tabular-nums w-28"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          onBlur={() => {
            const next = Number(amount);
            if (next !== bl.amount) apply({ amount: next });
          }}
        />
      </td>
      <td className="px-4 py-1.5 text-right">
        <div className="flex items-center justify-end gap-1.5">
          {isExternal && (
            <Input
              type="number"
              className="h-8 text-right tabular-nums w-24"
              value={estCost}
              placeholder="0"
              onChange={(e) => setEstCost(e.target.value)}
              onBlur={() => {
                const next = estCost === "" ? 0 : Number(estCost);
                if (next !== (bl.estimated_cost ?? 0)) apply({ estimated_cost: next });
              }}
            />
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-destructive"
            onClick={() => {
              if (confirm(`Slette "${bl.description}"?`)) {
                deleteMut.mutate({ id: bl.id, project_id: projectId });
              }
            }}
            title="Slett linje"
          >
            <SrIcon name="trash2" className="w-3.5 h-3.5" />
          </Button>
        </div>
      </td>
      <td className="px-4 py-1.5 text-right tabular-nums text-muted-foreground">
        {actualCost == null ? "—" : formatNOK(actualCost)}
      </td>
    </tr>
  );
}
